import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, CheckCircle2, Compass, Footprints, Globe2, Loader2, LocateFixed, MapPin, Rotate3d, Search, Sparkles, TriangleAlert } from 'lucide-react';
import { Button } from '../../Components/ui/button';
import { Input } from '../../Components/ui/input';
import { Card, CardContent } from '../../Components/ui/card';
import {
  CatalogueLandmark,
  checkStreetViewCoverage,
  fetchLandmarkCatalogue,
  LandmarkSearchResult,
  searchLandmarks,
  StreetViewCoverageResult,
} from '../../services/streetViewTourService';

declare global {
  interface Window {
    google?: any;
  }
}

type Coordinates = { lat: number; lng: number };

export interface GlobeLocationSelection extends Coordinates {
  name: string;
  address?: string;
  subject?: string;
  category?: string;
  country?: string;
  blurb?: string;
  panoId?: string;
  attribution?: string;
}

interface GoogleEarthGlobePickerProps {
  disabled?: boolean;
  onSelectLocation: (selection: GlobeLocationSelection & { coverage: StreetViewCoverageResult }) => void;
  onFallback: () => void;
}

let mapsApiPromise: Promise<void> | null = null;

function getMapsApiKey(): string {
  // Prefer the explicit public runtime config, but keep a build-time fallback.
  // Some standalone/route-loaded bundles do not execute viteEnv.ts before this
  // lazy component mounts, even though Vite has embedded the public key.
  return String(window.VITE_ENV?.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').trim();
}

function loadMapsApi(): Promise<void> {
  if (mapsApiPromise) return mapsApiPromise;
  const key = getMapsApiKey();
  if (!key) return Promise.reject(new Error('Google Maps 3D globe is not configured.'));

  mapsApiPromise = new Promise((resolve, reject) => {
    const callbackName = '__learnxrGoogleMapsReady';
    const globalWindow = window as Window & Record<string, unknown>;
    let settled = false;

    const finishWhenImportLibraryExists = () => {
      const deadline = Date.now() + 10_000;
      const check = () => {
        if (typeof window.google?.maps?.importLibrary === 'function') {
          settled = true;
          resolve();
          return;
        }
        if (Date.now() >= deadline) {
          settled = true;
          reject(new Error('Google Maps loaded without 3D Maps support.'));
          return;
        }
        window.setTimeout(check, 25);
      };
      check();
    };

    const fail = () => {
      if (!settled) {
        settled = true;
        reject(new Error('Google Maps failed to load.'));
      }
    };

    if (typeof window.google?.maps?.importLibrary === 'function') {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-learnxr-google-maps="true"]');
    if (existing) {
      existing.addEventListener('load', finishWhenImportLibraryExists, { once: true });
      existing.addEventListener('error', fail, { once: true });
      if (existing.readyState === 'complete') finishWhenImportLibraryExists();
      return;
    }

    globalWindow[callbackName] = finishWhenImportLibraryExists;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?loading=async&v=weekly&key=${encodeURIComponent(key)}&libraries=maps3d,marker&callback=${callbackName}`;
    script.dataset.learnxrGoogleMaps = 'true';
    script.onload = finishWhenImportLibraryExists;
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return mapsApiPromise;
}

function catalogueToSelection(landmark: CatalogueLandmark): GlobeLocationSelection {
  return {
    name: landmark.name,
    lat: landmark.lat,
    lng: landmark.lng,
    subject: landmark.subject,
    category: landmark.category,
    country: landmark.country,
    blurb: landmark.blurb,
    panoId: landmark.panoId,
    attribution: landmark.attribution,
  };
}

function searchToSelection(result: LandmarkSearchResult): GlobeLocationSelection {
  return {
    name: result.name,
    address: result.address,
    lat: result.coverage.lat ?? result.lat,
    lng: result.coverage.lng ?? result.lng,
    panoId: result.coverage.panoId,
    attribution: result.coverage.copyright,
  };
}

export function GoogleEarthGlobePicker({ disabled, onSelectLocation, onFallback }: GoogleEarthGlobePickerProps) {
  const mapHostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const locationsRef = useRef<GlobeLocationSelection[]>([]);
  const [landmarks, setLandmarks] = useState<CatalogueLandmark[]>([]);
  const [selected, setSelected] = useState<GlobeLocationSelection | null>(null);
  const [coverage, setCoverage] = useState<StreetViewCoverageResult | null>(null);
  const [loadingCoverage, setLoadingCoverage] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<LandmarkSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const allLocations = useMemo(
    () => [...landmarks.map(catalogueToSelection), ...searchResults.map(searchToSelection)],
    [landmarks, searchResults]
  );
  locationsRef.current = allLocations;

  const selectLocation = useCallback(async (location: GlobeLocationSelection) => {
    setSelected(location);
    setCoverage(null);
    setLoadingCoverage(true);
    try {
      const result = await checkStreetViewCoverage(location.lat, location.lng);
      setCoverage(result);
    } catch (error) {
      setCoverage({ available: false });
      setMapError(error instanceof Error ? error.message : 'Could not check Street View coverage.');
    } finally {
      setLoadingCoverage(false);
    }
  }, []);

  const focusLocation = useCallback((location: Coordinates) => {
    if (!mapRef.current) return;
    mapRef.current.center = { lat: location.lat, lng: location.lng, altitude: 0 };
    mapRef.current.range = 80_000;
    mapRef.current.tilt = 55;
  }, []);

  const resetWorld = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.center = { lat: 20, lng: 0, altitude: 0 };
    mapRef.current.range = 20_000_000;
    mapRef.current.tilt = 0;
    mapRef.current.heading = 0;
  }, []);

  const drawMarkers = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !window.google?.maps?.importLibrary) return;
    const { Marker3DInteractiveElement } = await window.google.maps.importLibrary('maps3d');
    const { PinElement } = await window.google.maps.importLibrary('marker');

    markersRef.current.forEach((marker) => marker.remove?.());
    markersRef.current = locationsRef.current.map((location) => {
      const marker = new Marker3DInteractiveElement({
        position: { lat: location.lat, lng: location.lng, altitude: 0 },
        title: location.name,
        drawsWhenOccluded: true,
      });
      marker.append(new PinElement({ background: '#7c3aed', borderColor: '#ffffff', glyphColor: '#ffffff', scale: 1.1 }));
      marker.addEventListener('gmp-click', () => {
        void selectLocation(location);
        focusLocation(location);
      });
      map.append(marker);
      return marker;
    });
  }, [focusLocation, selectLocation]);

  useEffect(() => {
    let cancelled = false;
    void loadMapsApi()
      .then(async () => {
        if (cancelled || !mapHostRef.current || mapRef.current) return;
        const { Map3DElement } = await window.google.maps.importLibrary('maps3d');
        const map = new Map3DElement({
          center: { lat: 20, lng: 0, altitude: 0 },
          range: 20_000_000,
          tilt: 0,
          heading: 0,
          mode: 'SATELLITE',
        });
        map.className = 'h-full w-full';
        mapHostRef.current.appendChild(map);
        mapRef.current = map;
        map.addEventListener('gmp-click', (event: any) => {
          const lat = Number(event?.latLng?.lat?.());
          const lng = Number(event?.latLng?.lng?.());
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            const location = { name: 'Selected globe location', lat, lng };
            void selectLocation(location);
            focusLocation(location);
          }
        });
        await drawMarkers();
      })
      .catch((error) => {
        if (!cancelled) setMapError(error instanceof Error ? error.message : 'Google Maps is unavailable.');
      });
    return () => {
      cancelled = true;
      markersRef.current.forEach((marker) => marker.remove?.());
      markersRef.current = [];
      mapRef.current?.remove?.();
      mapRef.current = null;
    };
  }, [focusLocation, selectLocation]);

  useEffect(() => {
    if (mapRef.current) void drawMarkers();
  }, [drawMarkers]);

  useEffect(() => {
    let cancelled = false;
    fetchLandmarkCatalogue()
      .then(({ landmarks: loadedLandmarks }) => {
        if (!cancelled) setLandmarks(loadedLandmarks);
      })
      .catch((error) => {
        if (!cancelled) setMapError(error instanceof Error ? error.message : 'Could not load curated landmarks.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = async () => {
    const query = searchText.trim();
    if (query.length < 2) return;
    setSearching(true);
    setMapError(null);
    try {
      setSearchResults(await searchLandmarks(query, { limit: 8 }));
    } catch (error) {
      setSearchResults([]);
      setMapError(error instanceof Error ? error.message : 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const handlePreview = () => {
    if (!selected || !coverage?.available) return;
    onSelectLocation({ ...selected, coverage });
  };

  return (
    <div className="space-y-3">
      <div className="relative min-h-[560px] overflow-hidden rounded-2xl border border-white/10 bg-[#07101f] p-1 shadow-2xl shadow-indigo-950/20">
        <div ref={mapHostRef} className="absolute inset-1 overflow-hidden rounded-[calc(1rem-2px)]" aria-label="Interactive 3D globe" />
        <div className="pointer-events-none absolute inset-x-5 top-5 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="pointer-events-auto flex-1 space-y-1.5">
            <p className="px-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">Discover a teachable place</p>
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void runSearch();
                }
              }}
              placeholder="Search a landmark, city, or street…"
              className="h-12 rounded-xl border-white/15 bg-slate-950/80 pr-10 text-white shadow-2xl backdrop-blur-xl placeholder:text-white/45"
              disabled={disabled || searching}
            />
            {searching ? <Loader2 className="absolute right-4 top-[calc(50%+10px)] h-4 w-4 animate-spin text-white" /> : <Search className="absolute right-4 top-[calc(50%+10px)] h-4 w-4 text-white/55" />}
          </div>
          <Button type="button" onClick={() => void runSearch()} disabled={disabled || searching || searchText.trim().length < 2} className="pointer-events-auto h-12 rounded-xl bg-white px-5 text-slate-950 shadow-2xl hover:bg-white/90">
            <Sparkles className="mr-2 h-4 w-4" /> Explore
          </Button>
        </div>
        <div className="pointer-events-none absolute right-5 top-5 flex gap-2">
          <div className="rounded-full border border-emerald-300/20 bg-slate-950/65 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200 shadow-xl backdrop-blur-xl">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_10px_#86efac]" /> Live 3D world
          </div>
        </div>
        <div className="pointer-events-auto absolute bottom-5 left-5 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={resetWorld} className="rounded-full border-white/10 bg-slate-950/70 text-white shadow-xl backdrop-blur-xl hover:bg-slate-900">
            <Globe2 className="mr-1.5 h-3.5 w-3.5" /> Reset world
          </Button>
          {selected && <Button type="button" variant="secondary" size="sm" onClick={() => focusLocation(selected)} className="rounded-full border-white/10 bg-slate-950/70 text-white shadow-xl backdrop-blur-xl hover:bg-slate-900"><LocateFixed className="mr-1.5 h-3.5 w-3.5" /> Focus place</Button>}
          <Button type="button" variant="secondary" size="sm" onClick={onFallback} className="rounded-full border-white/10 bg-slate-950/70 text-white shadow-xl backdrop-blur-xl hover:bg-slate-900">
            Use 2D map
          </Button>
        </div>
        <div className="pointer-events-none absolute bottom-5 right-5 hidden max-w-[240px] items-center gap-2 rounded-xl border border-white/10 bg-slate-950/65 px-3 py-2 text-[11px] text-white/75 shadow-xl backdrop-blur-xl md:flex">
          <Rotate3d className="h-4 w-4 shrink-0 text-violet-300" /> Drag to orbit · scroll to zoom · select a marker to inspect Street View
        </div>
        <div className="pointer-events-none absolute left-5 top-[7.4rem]">
          <div className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-1.5 text-[11px] text-white/70 shadow-xl backdrop-blur-xl">{landmarks.length} curated learning places</div>
        </div>
      </div>

      {mapError && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs flex items-center gap-2">
          <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="flex-1">{mapError}</span>
          <Button type="button" variant="outline" size="sm" onClick={onFallback}>Open fallback map</Button>
        </div>
      )}

      {selected && (
        <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-background to-primary/[0.04] shadow-lg">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" /> {selected.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{selected.address || [selected.country, selected.subject].filter(Boolean).join(' · ') || `${selected.lat.toFixed(4)}, ${selected.lng.toFixed(4)}`}</p>
                {selected.blurb && <p className="text-xs mt-2 text-muted-foreground">{selected.blurb}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {loadingCoverage ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking coverage</span> : coverage?.available ? <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Street View available</span> : <span className="text-xs text-amber-600 flex items-center gap-1"><TriangleAlert className="w-3.5 h-3.5" /> No Street View here</span>}
              </div>
            </div>
            {coverage && (
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                {coverage.date && <span>Imagery {coverage.date}</span>}
                {typeof coverage.linkCount === 'number' && <span className="flex items-center gap-1"><Footprints className="w-3 h-3" /> {coverage.walkable ? `Walkable · ${coverage.linkCount} paths` : 'Single view'}</span>}
                <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> Teacher preview</span>
                {coverage.copyright && <span>{coverage.copyright}</span>}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={handlePreview} disabled={disabled || loadingCoverage || !coverage?.available} className="shadow-sm">
                <Compass className="mr-1.5 h-3.5 w-3.5" /> Load immersive preview
              </Button>
              {selected.panoId && <span className="text-[11px] text-muted-foreground self-center">Verified panorama ready</span>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default GoogleEarthGlobePicker;
